import Chart from 'react-apexcharts';
import moment from 'moment';
import { memo } from 'react';
import { CONSTANTS } from '../../../constants/constants';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/material/styles';
import { useChartA11y } from '../../../hooks/useChartA11y';

const { YEAR_MONTH_DAY_TIME_DASHED } = CONSTANTS.DATE_FORMAT;
const useStyles = makeStyles(theme => ({
  cursorPointer: { cursor: 'pointer' },
  mainChart: {
    ...theme.apexchartsOverrides
  }
}));

function NRLineChart({
  chartContainerID = '',
  type = 'bar',
  width = '100%',
  height = '100%',
  title,
  a11yTitle,
  subtitle,
  seriesName,
  data = [],
  categories = [],
  colors,
  exportFileNamePrefix,
  yAxisTitle,
  barWidth = '70%',
  tooltipTheme = 'light',
  events,
  filters,
  toolbarOffsetY = 10,
  onLabelClick,
  logarithmic = false,
  /** When true, disables Apex grow animations. Use for print / react-to-print so the snapshot is not mid-animation. */
  disableAnimation = false
}) {
  const theme = useTheme();
  const classes = useStyles();
  const exportFileName = `${exportFileNamePrefix}${moment().format(YEAR_MONTH_DAY_TIME_DASHED)}`;
  const series = [
    {
      name: seriesName,
      data
    }
  ];

  useChartA11y({
    chartContainerID,
    title: a11yTitle,
    labels: categories,
    series: series
  });

  // ApexCharts only fires `updated` after chart.update()/updateSeries — not on first render (`mounted`).
  // Defer DOM work so x-axis labels exist after layout (avoids missing tabindex until a resize/devtools).
  const attachEventListenerToDataLabel = (chartContext, w) => {
    if (!w?.config?.onLabelClick) return;
    const runAttach = () => {
      const root = chartContext?.el;
      if (!root) return;
      const labels = root.querySelectorAll('.apexcharts-text.apexcharts-xaxis-label tspan');

      labels.forEach(item => {
        const labelText = item.childNodes[0]?.nodeValue ?? item.textContent?.trim();
        if (labelText == null || labelText === '') return;
        const categoryIndex = w.config.xaxis.categories.indexOf(labelText);

        if (categoryIndex !== -1 && !item['hasDrawerListener']) {
          item['hasDrawerListener'] = true;
          item.setAttribute('role', 'button');
          item.setAttribute('tabindex', '0');
          item.setAttribute('aria-label', `Open detailed drawer for ${labelText}`);
          item.setAttribute('aria-expanded', 'false');
          item.setAttribute('aria-controls', `drawer-${categoryIndex}`);
          item.setAttribute('aria-haspopup', 'dialog');
          item.setAttribute('aria-describedby', `drawer-${categoryIndex}-description`);
          item.setAttribute('aria-labelledby', `drawer-${categoryIndex}-title`);
          item.addEventListener('click', () => {
            w.config.onLabelClick(w, categoryIndex);
          });
        }
      });
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(runAttach);
    });
  };

  function dataPointMouseEnter(event, chartContext, config) {
    if (event && !!event.currentTarget) event.currentTarget.style.cursor = 'pointer';
  }

  const options = {
    ...(onLabelClick && { onLabelClick }),
    ...(filters && { filters }),
    chart: {
      events: {
        ...(events && { ...events }),
        ...(!!onLabelClick && {
          mounted: attachEventListenerToDataLabel,
          updated: attachEventListenerToDataLabel
        }),
        dataPointMouseEnter
      },
      type: type,
      height: 350,
      stacked: true,
      animations: {
        enabled: !disableAnimation
      },
      toolbar: {
        show: true,
        offsetY: toolbarOffsetY,
        export: {
          ...(!!exportFileNamePrefix && {
            csv: {
              filename: exportFileName
            },
            svg: {
              filename: exportFileName
            },
            png: {
              filename: exportFileName
            }
          })
        }
      },
      zoom: {
        enabled: true
      }
    },
    stroke: {
      curve: 'smooth',
      colors: theme.palette.neutral[700],
      width: 2
    },
    markers: {
      size: 3,
      //colors: undefined,
      strokeColors: theme.palette.white,
      strokeWidth: 2,
      strokeOpacity: 0.9,
      strokeDashArray: 0,
      fillOpacity: 1,
      discrete: [],
      shape: 'circle',
      radius: 2,
      offsetX: 0,
      offsetY: 0,
      onClick: undefined,
      onDblClick: undefined,
      showNullDataPoints: true,
      hover: {
        size: undefined,
        sizeOffset: 2
      }
    },
    plotOptions: {
      bar: {
        columnWidth: barWidth,
        borderRadius: theme.shape.borderRadius,
        borderRadiusApplication: 'end', // Fix bottom border added with upgraded version
        horizontal: false,
        dataLabels: {
          hideOverflowingLabels: false,
          position: 'top' // top, center, bottom
        }
      }
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      formatter: function (val, opts) {
        // Return the actual value from the data array
        return opts.w.config.series[0].data[opts.dataPointIndex];
      },
      style: {
        ...theme.typography.caption,
        colors: [theme.palette.text.primary]
      }
    },
    xaxis: {
      type: 'category',
      categories,
      title: 'Categories',
      labels: {
        style: {
          colors: theme.palette.text.secondary,
          ...theme.typography.caption,
          cssClass: !!onLabelClick ? classes.cursorPointer : ''
        }
      },
      axisBorder: {
        color: theme.palette.divider
      }
    },
    grid: {
      // hide x lines -> https://apexcharts.com/docs/options/grid/
      borderColor: theme.palette.divider,
      xaxis: {
        lines: {
          show: false
        }
      }
    },
    yaxis: [
      {
        seriesName: seriesName,
        logarithmic: logarithmic,
        title: {
          text: yAxisTitle
        },
        labels: {
          formatter: function (val) {
            return Math.round(val);
          },
          style: {
            colors: theme.palette.text.secondary,
            ...theme.typography.caption
          }
        },
        showAlways: true
      }
    ],
    legend: {
      position: 'right',
      offsetY: 40,
      showForSingleSeries: false //Show legend even if there is just 1 series.
    },
    fill: {
      opacity: 1
    },
    colors: colors || [theme.palette.neutral[900]],
    title: {
      text: title,
      align: 'left',
      margin: 10,
      offsetX: 0,
      offsetY: 20,
      floating: false,
      style: {
        fontSize: '1rem',
        fontWeight: '700',
        fontFamily: undefined,
        color: theme.palette.secondary.main
      }
    },
    subtitle: {
      text: subtitle,
      align: 'left',
      margin: 10,
      offsetX: 0,
      offsetY: 30,
      floating: false,
      style: {
        fontSize: '12px',
        fontWeight: 'normal',
        fontFamily: undefined,
        color: theme.palette.secondary.light
      }
    },
    tooltip: {
      theme: tooltipTheme
    }
  };

  return (
    <div id={`${chartContainerID}`} tabIndex={0}>
      <Chart options={options} series={series} type={type} height={height} width={width} className={classes.mainChart} />
    </div>
  );
}

// Memoize to avoid expensive ApexCharts re-instantiation when an ancestor
// (e.g. the asset actions menu, or the printable container while it's
// mounting) re-renders without changing this chart's actual props.
export default memo(NRLineChart);
